import React, { useState, useEffect, useCallback, useMemo, RefObject, useRef } from 'react';
import AddNewServiceWrapper from 'views/AddNewServices/AddSingleService/common/AddNewServiceWrapper';
import { dynamicTranslate, translate } from 'components/Translation';
import _, { cloneDeep } from 'lodash';
import PlansServicesTab from '../tabs/PlansServicesTab';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { env } from 'config/app.config';
import SelectPlanDialog from 'views/AddNewServices/AddSingleService/tabs/common/SelectPlanDialog';
import { AccountsService } from 'services/accounts/accounts';
import { useSelector } from 'react-redux';
import { accountNameSelector, emailSelector, upIdSelector, userNameSelector } from 'redux/modules/session/selectors';
import {
	getCommonLabels,
	getEmailAddress,
	getFiberProLabels,
	getTemplateKeys,
} from 'redux/modules/dynamicLabels/selectors';
import { dispatch } from 'redux/store';
import { showLoadingIndicator, hideLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import LocationTab from '../tabs/LocationTab';
import LinesOneLine from 'views/AddNewServices/AddSingleService/tabs/common/LinesOneLine';
import { v4 as uuidv4 } from 'uuid';
import FBBTable from 'components/ServicesTables/FBBTable';
import DialogTermsAndConditions from 'views/Numbers/Details/Services/subcomponents/common/DialogTermsAndConditions';
import { useHistory } from 'react-router-dom';
import {
	MIN_LINE_NAME_LENGTH,
	portalStg,
	portalPrd,
	termsAndConstionsUrl,
} from 'views/AddNewServices/AddSingleService/constants';
import { IAlternativeSelectItem } from 'components/AlternativeSelect/AlternativeSelect';
import LinesTabFBB from '../tabs/LinesTabFBB';
import LineFbb from '../tabs/common/LineFbb';
import ContactDetailsTabFBB from '../tabs/ContactDetailsTabFBB';
import PlanDetail from '../tabs/common/PlanDetail';
import FilesTabFBB from '../tabs/FilesTabFBB';
import OrderSummaryTab from '../tabs/OrderSummaryTab';
import { getLanguage } from 'utils/language/language';
import moment from 'moment';
import { validateUserName } from 'utils/userNameValidate';
import { fiberPro2b, isBtlOfferOn, isFiberProOn, wfbb } from 'utils/killswitch';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import SendDialogFiberPro from '../tabs/common/SendDialogFiberPro';
import FbbContactSummaryTab from '../tabs/FbbContactSummaryTab';
import idConfig from 'utils/ecommerce/services/id.config';
import { extract } from 'query-string';
import { CustomAttribute, extractAttributeValue } from 'utils/ecommerce/services/extract';
import { LineDetails } from 'utils/api/payloads/omantelMicroApi';
import { loadQualtrics } from 'utils/qualtrics';

enum STEPS {
	Location = 0,
	Lines,
	PlansServices,
	Files,
	ContactDetails,
	Summary,
	Order,
}

const newLine: () => types.services.fixed.fixedBusinessBroadband.ILine = () => ({
	name: '',
	additionalServices: [],
	status: 'idle',
	id: uuidv4(),
	isNameValid: false,
});

interface IIndexProps {
	back: () => void;
	mainContainerRef?: React.RefObject<HTMLDivElement>;
	account: IAlternativeSelectItem | null;
	skipLocation?: boolean; // for unit test case skipped location
}

const Index = (props: IIndexProps) => {
	const { back: handleBack, mainContainerRef } = props;
	// skipped location step for dev env
	const [activeTab, setActiveTab] = useState<STEPS>(
		env === 'DEV' || props.skipLocation ? STEPS.Lines : STEPS.Location
	);
	const [sendDialog, setSendDialog] = useState<boolean>(false);
	const [showError, setShowError] = useState<boolean>(false);
	const [TcUrl, setTCTextUrl] = useState<string>('');
	const [scrollToButton, setScrollToButton] = useState<boolean>(false);
	const [termsAndConditionsCheckbox, setTermsAndConditionsCheckbox] = useState<boolean>(false);
	const [address, setAddress] = useState<{ [key: string]: string }>({});
	const [lines, setLines] = useState<Array<types.services.fixed.fixedBusinessBroadband.ILine>>([newLine()]);
	const [gis, setGis] = useState<string>('');
	const [karokie, setKarokie] = useState<{ content?: string; name?: string }>({});
	const [contactDetails, setContactDetails] = useState<types.services.fixed.IContactDetailsFiberPro>({
		reqName: '',
		contactNo: '',
		bestTime: '',
		workingNumber: '',
	});
	const [addPlanDialog, setAddPlanDialog] = useState<{
		show: boolean;
		ind: number;
		line?: types.services.fixed.fixedBusinessBroadband.ILine;
	}>({
		show: false,
		ind: -1,
	});
	const history = useHistory();
	const [checkingName, setCheckingName] = useState<String[]>([]);
	const [checkingNameErrors, setCheckingNameError] = useState<{}>({});
	const lang = getLanguage();
	const email = useSelector(emailSelector);
	const upId = useSelector(upIdSelector);
	const fiberProLabels = useSelector(getFiberProLabels);
	const commonLabels = useSelector(getCommonLabels);
	const remoteConfig = useSelector(remoteConfigSelector);
	const isFiberPro = isFiberProOn(remoteConfig); // kill switch to hide fbb pro plan
	const accountId = props.account?.id || '';
	const accountName = useSelector(accountNameSelector);
	const [loading, setLoading] = useState(false);
	const [location, setLocation] = useState<{ [key: string]: string }>({});
	const [orderSummary, setOrderSummary] = useState<types.services.fixed.fixedBusinessBroadband.OrderSummary>({
		summary: '',
		currency: '',
		lineDetails: [],
	});
	const templateKeys = useSelector(getTemplateKeys);
	const emailAddress = useSelector(getEmailAddress);
	const isFiberPro2b = fiberPro2b(remoteConfig); // kill switch to show fiber pro 2b changes
	const isWfbb = wfbb(remoteConfig); // kill switch to show fiber pro 2b changes

	useEffect(() => {
		setTCTextUrl(`${env === 'STG' || env === 'DEV' ? portalStg : portalPrd}${lang}${termsAndConstionsUrl}`);
	}, [lang]);
	const getServiceDetails = useCallback((line: types.services.fixed.fixedBusinessBroadband.ILine) => {
		return {
			categoryId: idConfig.FBB.FBB_FBB_PRO_PLANS[env],
			productId: line.plan!.partNumber,

			additionalService: {
				categoryId: idConfig.FBB.ADDITIONAL_SERVICES[env],
				productIds: [...line.additionalServices.map(({ partNumber }) => partNumber)],
			},
			staticService: {
				categoryId: idConfig.FBB.STATIC_IP[env],
				productIds: line.staticIP?.mainProduct?.partNumber
					? [
							line.staticIP?.mainProduct?.partNumber || '',
							line.staticIP?.modes?.partNumber || '',
							line.staticIP?.configs?.partNumber || '',
					  ]
					: [],
			},
		};
	}, []);
	useEffect(() => {
		if (activeTab === STEPS.Order) {
			setLoading(true);
			const lineDetails: any = lines.map((line, ind) => {
				if (isFiberPro2b) {
					return {
						...getServiceDetails(line),
						adslUserName: line.name,
					};
				} else {
					return {
						adslUserName: line.name,
						productId: line.plan!.partNumber,
						requiredServiceProductIds: [line.requiredService!.partNumber],
						staticServiceIds: line.staticIP ? [line.staticIP?.mainProduct] : [],
						additionalServiceProductIds: [...line.additionalServices.map(({ partNumber }) => partNumber)],
					};
				}
			});
			AccountsService.getFbbOrderSummary({ lineDetails: lineDetails }).subscribe((res) => {
				if (res.response.data) {
					setLoading(false);
					setOrderSummary(res.response.data);
				}
			});
		}
	}, [activeTab, lines.length, lang]);
	const sendOneRequest = async (retry: boolean = false) => {
		const setStatus = (status: 'progress' | 'idle' | 'failure' | 'success', ind: number) => {
			setLines((lines) => {
				const newLines = cloneDeep(lines);
				newLines[ind] = {
					...newLines[ind],
					status,
				};
				return newLines;
			});
		};
		const getLineDetails = (retry: boolean) => {
			let selectedLines = lines;
			lines.map((line, ind) => {
				if (retry === true) {
					if (line.status === 'failure') setStatus('progress', ind);
				} else {
					setStatus('progress', ind);
				}
			});
			if (retry === true) {
				selectedLines = lines.filter((line) => line.status === 'failure');
			}
			const lineSelectDetails: any = selectedLines.map((line, ind) => {
				let selectedServices = [
					...line.additionalServices.map(({ name }) => name),
					line.staticIP?.mainProduct?.shortDescription,
				].join(',');
				let lastChar = selectedServices.slice(-1);
				if (lastChar == ',') {
					selectedServices = selectedServices.slice(0, -1);
				}
				if (isFiberPro2b) {
					let payload: LineDetails = {
						...getServiceDetails(line),
						bandwidth: line.plan!.download!,
						requestedPlan: line.plan?.shortDescription ?? '',
						selectedServices: selectedServices,
						userName: line.name,
						templateKey: templateKeys.createFbbOrder,
					};
					if (isWfbb) {
						payload = {
							...payload,
							salesLeads: [
								// Optional only need to send in case of Fiber pro plan
								{
									name: line.plan?.wfbbProductName,
									productId: line.plan?.wfbbProductId,
									emailAddress: emailAddress.wfbb,
									templateKey: templateKeys.wfbbCreate, // wfbb_direct_contact_body / wfbb_direct_contact_subject
								},
							],
						};
					}
					return payload;
				} else {
					return {
						plansSelected: [
							line.plan!.partNumber,
							line.plan!.tariffPlanId,
							line.plan!.provisioningId,
							line.requiredService!.partNumber,
							...line.additionalServices.map(({ partNumber }) => partNumber),
							line.staticIP?.mainProduct,
						].join(','),
						bandwidth: line.plan!.download!,
						requestedPlan: line.plan?.shortDescription ?? '',
						selectedServices: selectedServices,
						userName: line.name,
					};
				}
			});
			return lineSelectDetails;
		};

		dispatch(showLoadingIndicator());
		try {
			const accounts = await AccountsService.getCorpAccountsForCurrentUser().toPromise();
			return AccountsService.createFbbOrder(
				{
					upId: parseInt(upId),
					sendSmsToUser: true,
					firstName: contactDetails.reqName,
					preferredTimeToContact: contactDetails.bestTime,
					contactNumber1: contactDetails.contactNo,
					contactNumber2: contactDetails.workingNumber,
					emailId: email,
					gisDetails: gis,
					idExpiryDate: moment()
						.add(1, 'month')
						.format('YYYY-MM-DD'),
					documents: [karokie],
					templateKey: templateKeys.createFbbOrder,
					lineDetails: getLineDetails(retry),
					customerName: accountName,
					customerId: String(
						accounts?.accounts?.find((account) => account.accountId === accountId)?.documentId || ''
					),
				},
				isFiberPro2b
			).subscribe(
				(r) => {
					// @ts-ignore
					hideLoadingIndicator();
					if (r?.response?.data) {
						r?.response?.data.forEach((detail: any) => {
							const index = lines.findIndex((line) => {
								return line.name === detail.adslUserName;
							});
							if (index !== -1) {
								setLines((lines) => {
									const newLines = cloneDeep(lines);
									newLines[index] = {
										...newLines[index],
										status: detail.requestId ? 'success' : 'failure',
										requestId: detail.requestId,
									};
									return newLines;
								});
							}
						});
					}
				},
				() => {
					lines.map((line, ind) => {
						setStatus('failure', ind);
					});
				}
			);
		} catch (error) {
			console.log(error);
			lines.map((line, ind) => {
				setStatus('progress', ind);
			});
		}
	};

	const handleAddKarokie = (file: { content?: string; name?: string }) => {
		setKarokie((state) => ({ ...state, ...file }));
	};

	const back = () => {
		setActiveTab((activeTab) => {
			switch (activeTab) {
				case STEPS.Location:
					handleBack();
					return STEPS.Location;

				case STEPS.Lines:
					return STEPS.Location;

				case STEPS.PlansServices:
					return STEPS.Lines;

				case STEPS.Files:
					return STEPS.PlansServices;

				case STEPS.ContactDetails:
					return STEPS.Files;

				case STEPS.Order:
					return STEPS.Summary;

				case STEPS.Summary:
					return STEPS.ContactDetails;
			}
		});
	};

	const nextStep = () => {
		setActiveTab((activeTab) => {
			switch (activeTab) {
				case STEPS.Location:
					return STEPS.Lines;

				case STEPS.Lines:
					return STEPS.PlansServices;

				case STEPS.PlansServices:
					return STEPS.Files;

				case STEPS.Files:
					return STEPS.ContactDetails;

				case STEPS.ContactDetails:
					return STEPS.Summary;

				case STEPS.Summary:
					return STEPS.Order;

				case STEPS.Order:
					setShowError(!termsAndConditionsCheckbox);
					if (termsAndConditionsCheckbox) {
						setSendDialog(true);
						sendOneRequest();
					}
					return STEPS.Order;
			}
		});
	};

	const handleEditName = useCallback(
		(reverse: boolean) => (name: string, ind: number) => {
			name = name.toLowerCase();
			setLines((lines) => {
				const newLines = lines.map((line) => _.cloneDeep(line));
				newLines[reverse ? lines.length - ind - 1 : ind].name = name;
				newLines[reverse ? lines.length - ind - 1 : ind].isNameValid = false;
				const findSameNameLine = lines.find((line) => line.name === name);
				const isUserNameValid = validateUserName(name, fiberProLabels);
				if (!isUserNameValid?.isValid) {
					setCheckingNameError((oldErrors) => ({
						...oldErrors,
						[`name-${ind}`]: isUserNameValid?.error,
					}));
				} else {
					if (findSameNameLine) {
						setCheckingNameError((oldErrors) => ({
							...oldErrors,
							[`name-${ind}`]: dynamicTranslate(fiberProLabels.alreadyUserNameError, name),
						}));
					} else {
						lines.forEach((line, index) => {
							setCheckingNameError((oldErrors) => ({ ...oldErrors, [`name-${index}`]: undefined }));
						});
					}
				}

				return newLines;
			});
		},
		[checkingNameErrors]
	);

	const handleDeleteLine = useCallback(
		(reverse: boolean) => (ind: number) => {
			setCheckingNameError((oldErrors) => ({ ...oldErrors, [`name-${ind}`]: undefined }));
			setLines((lines) =>
				lines.length > 1
					? [...lines].filter((_, elemInd) => elemInd !== (reverse ? lines.length - 1 - ind : ind))
					: [...lines]
			);
		},
		[]
	);

	const [nextStepDisabled, setNextStepDisabled] = useState<{
		[key: number]: types.services.fixed.INextStepDisabled;
	}>(
		Object.keys(STEPS).reduce(
			(acc, key) => ({
				...acc,
				[key]: {
					card: false,
					additional: false,
				},
			}),
			{}
		)
	);

	const setNextStepCardDisabled = useCallback(
		(nextStepCardDisabled: boolean) => {
			setNextStepDisabled((nextStepDisabled) => ({
				...nextStepDisabled,
				[activeTab]: {
					...nextStepDisabled[activeTab],
					card: nextStepCardDisabled,
				},
			}));
		},
		[activeTab, setNextStepDisabled]
	);
	useEffect(() => {
		if (activeTab === STEPS.Location && Object.keys(address).length > 0) {
			setScrollToButton(true);
		}
	}, [address]);
	useEffect(() => {
		setNextStepDisabled((nextStepDisabled) => {
			const setValue = (additional: boolean) => ({
				...nextStepDisabled,
				[activeTab]: {
					...nextStepDisabled[activeTab],
					additional,
				},
			});

			switch (activeTab) {
				case STEPS.Lines: {
					return setValue(
						lines.some(({ name, isNameValid }) => name.trim().length < MIN_LINE_NAME_LENGTH) ||
							!Object.values(checkingNameErrors).every((error) => error === undefined)
					);
				}
				case STEPS.PlansServices:
					return setValue(
						lines.some(({ name, isNameValid }) => name.trim().length < MIN_LINE_NAME_LENGTH || !isNameValid)
					);

				case STEPS.Files:
					return setValue(!karokie.content);

				case STEPS.Order:
					return setValue(!termsAndConditionsCheckbox);

				default:
					return nextStepDisabled;
			}
		});
	}, [activeTab, setNextStepDisabled, lines, termsAndConditionsCheckbox, karokie, checkingNameErrors]);

	const deletePlan = (ind: number) =>
		setLines((lines) => {
			const newLines = cloneDeep(lines);
			newLines[ind] = {
				...newLine(),
				name: newLines[ind].name,
				isNameValid: newLines[ind].isNameValid,
			};
			return newLines;
		});

	const editPlan = useCallback(
		(ind: number) =>
			setAddPlanDialog(() => ({
				show: true,
				ind,
				line: lines[ind],
			})),
		[lines, setAddPlanDialog]
	);

	const linesEl = useMemo(
		() =>
			[...lines].map((line, ind) => (
				<LinesOneLine
					line={line}
					key={line.id}
					handleEditName={handleEditName(false)}
					error={checkingNameErrors[`name-${ind}`]}
					handleRemoveItem={handleDeleteLine(false)}
					ind={ind}
					lines={lines}
				/>
			)),
		[lines, handleEditName, handleDeleteLine]
	);

	const handleUpdateNameValidation = useCallback(
		(reverse: boolean) => (isValid: boolean, ind: number) => {
			setLines((lines) => {
				const newLines = lines.map((line) => _.cloneDeep(line));
				newLines[reverse ? lines.length - ind - 1 : ind].isNameValid = isValid;
				return newLines;
			});
		},
		[]
	);
	const checkLineNameValidate = useCallback(
		(name: string, ind: number) => {
			setCheckingName((oldNames) => [...oldNames, `name-${ind}`]);
			AccountsService.validateUserName(name).subscribe(
				({ response }) => {
					if (response.data.available) {
						handleUpdateNameValidation(false)(true, ind);
						setCheckingNameError((oldErrors) => ({ ...oldErrors, [`name-${ind}`]: undefined }));
					} else {
						handleUpdateNameValidation(false)(false, ind);
						setCheckingName((oldNames) => oldNames.filter((name) => name !== `name-${ind}`));
						setCheckingNameError((oldErrors) => ({
							...oldErrors,
							[`name-${ind}`]: dynamicTranslate(fiberProLabels.alreadyUserNameError, name),
						}));
					}
					setCheckingName((oldNames) => oldNames.filter((name) => name !== `name-${ind}`));
				},
				(error) => {
					handleUpdateNameValidation(false)(false, ind);
					setCheckingName((oldNames) => oldNames.filter((name) => name !== `name-${ind}`));
					setCheckingNameError((oldErrors) => ({ ...oldErrors, [`name-${ind}`]: error.response?.details }));
				}
			);
		},
		[handleUpdateNameValidation]
	);

	const planLinesEl: Array<types.services.fixed.IPlansServicesLines> = useMemo(() => {
		if (activeTab === STEPS.PlansServices)
			return lines.map((line, ind) => ({
				lineEl: (
					<div>
						<LineFbb
							{...line}
							ind={ind + 1}
							handleEditName={(name: string) => handleEditName(false)(name, ind)}
							showNumber={false}
							iconActive={!!line.plan}
							minLineNameLength={MIN_LINE_NAME_LENGTH}
							validate={checkLineNameValidate}
							error={checkingNameErrors[`name-${ind}`]}
							loading={checkingName.includes(`name-${ind}`)}
						/>
					</div>
				),
				summaryEl: !!line.plan ? (
					<PlanDetail
						additionalServices={line.additionalServices}
						contract={translate(`add.new.line.contract.duration.${line.plan.contract}`)}
						subtitle={line.plan?.fbbPro ? fiberProLabels.fiberPro : fiberProLabels.fbb}
						planDetail={line.plan}
						description={line.requiredService?.name ?? ''}
						onEdit={() => editPlan(ind)}
					/>
				) : (
					undefined
				),
			}));
		else return [];
	}, [lines, handleEditName, editPlan, activeTab, checkingName, checkingNameErrors, checkLineNameValidate]);

	const orderEl: Array<types.services.fixed.IPlansServicesLines> = useMemo(() => {
		if (activeTab === STEPS.Order)
			return lines.map((line, ind) => ({
				lineEl: (
					<LineFbb
						{...line}
						ind={ind + 1}
						disableEdit
						showNumber={lines.length > 1}
						minLineNameLength={MIN_LINE_NAME_LENGTH}
						allowDelete={lines.length > 1 && !line.status ? true : false}
						handleRemoveItem={handleDeleteLine(false)}
					/>
				),
				summaryEl: !!line.plan ? (
					<PlanDetail
						additionalServices={line.additionalServices}
						contract={translate(`add.new.line.contract.duration.${line.plan.contract}`)}
						subtitle={line.plan?.fbbPro ? fiberProLabels.fiberPro : fiberProLabels.fbb}
						planDetail={line.plan}
						description={line.requiredService?.name ?? ''}
					/>
				) : (
					undefined
				),
			}));
		else return [];
	}, [activeTab, lines]);

	const hasPlan = useCallback((ind: number) => !!lines[ind].plan, [lines]);

	return (
		<AddNewServiceWrapper
			width={800}
			buttons={[
				{
					color: 'tertiary-light',
					text: activeTab === STEPS.Location ? commonLabels.cancel : commonLabels.back,
					onClick: () => back(),
				},
				{
					color: 'primary-light',
					text: activeTab === STEPS.Order ? commonLabels.orderNow : commonLabels.next,
					onClick: nextStep,
					ref: true,
					disabled: Object.values(nextStepDisabled[activeTab]).some(Boolean),
				},
			]}
			tabs={[
				{
					name: fiberProLabels.locTabName,
					title: fiberProLabels.locTabHeading,
					content: (
						<LocationTab
							setNextStepDisabled={setNextStepCardDisabled}
							setGis={setGis}
							setAddress={setAddress}
							location={location}
							setLocation={setLocation}
							setLocationInputs={(name: string, value: string) =>
								setLocation((location) => ({ ...location, [name]: value }))
							}
							address={address}
							mainContainerRef={mainContainerRef}
						/>
					),
				},
				{
					name: fiberProLabels.lineTabName,
					title: fiberProLabels.lineTabHeading,
					content: (
						<LinesTabFBB
							linesEl={linesEl}
							setNextStepDisabled={setNextStepCardDisabled}
							handleIncreaseNumberOfLines={() =>
								setLines((lines) => [{ ...newLine() }, ...lines.map((line) => _.cloneDeep(line))])
							}
						/>
					),
				},
				{
					name: fiberProLabels.planTabName,
					title: fiberProLabels.planTabHeading,
					content: (
						<>
							<PlansServicesTab
								lines={planLinesEl}
								setNextStepDisabled={setNextStepCardDisabled}
								hasPlan={hasPlan}
								handleAddPlan={(ind: number) => setAddPlanDialog({ show: true, ind })}
								handleDeletePlan={deletePlan}
							/>
							<ConditionalRender
								show={addPlanDialog.show}
								onTrue={() => (
									<SelectPlanDialog
										isFbb={true}
										hideDialog={() => setAddPlanDialog({ show: false, ind: -1 })}
										TableComponent={FBBTable} // FBBTable is making tabs and calling plans apis
										planTitle={fiberProLabels.selectPlanHeading}
										line={addPlanDialog.line}
										handleAddPlan={(
											plan: types.services.fixed.IPlan,
											additionalServices: types.services.fixed.IAdditionalService[],
											{ selectedStaticIP }
										) => {
											setAddPlanDialog((addPlanDialog) => {
												setLines((lines) => {
													const newLines = cloneDeep(lines);
													newLines[addPlanDialog.ind] = {
														...newLines[addPlanDialog.ind],
														plan,
														additionalServices,
														staticIP: selectedStaticIP,
													};
													return newLines;
												});
												return {
													show: false,
													ind: -1,
												};
											});
										}}
									/>
								)}
							/>
						</>
					),
				},
				{
					name: fiberProLabels.fileTabName,
					title: fiberProLabels.fileTabHeading,
					content: (
						<FilesTabFBB
							workingNumber={contactDetails.workingNumber}
							handleAddKarokie={handleAddKarokie}
							setNextStepDisabled={setNextStepCardDisabled}
							showWorkingNumber={true}
							karokie={karokie}
							onChange={(name: keyof types.services.fixed.IContactDetails, value: string | boolean) => {
								setContactDetails((contactDetails) => ({ ...contactDetails, [name]: value }));
							}}
						/>
					),
				},
				{
					name: fiberProLabels.contactTabName,
					title: fiberProLabels.contactTabName,
					content: (
						<ContactDetailsTabFBB
							{...contactDetails}
							onChange={(
								name: keyof types.services.fixed.IContactDetailsFiberPro,
								value: string | boolean
							) => {
								setContactDetails((contactDetails) => ({ ...contactDetails, [name]: value }));
							}}
							setNextStepDisabled={setNextStepCardDisabled}
						/>
					),
				},
				{
					name: fiberProLabels.summaryTabName,
					title: fiberProLabels.summaryTabName,
					content: (
						<FbbContactSummaryTab
							setNextStepDisabled={setNextStepCardDisabled}
							location={location}
							contactDetails={contactDetails}
						/>
					),
				},
				{
					name: fiberProLabels.orderTabName,
					title: fiberProLabels.orderSumm,
					content: (
						<>
							<OrderSummaryTab
								termsAndConditions={
									<DialogTermsAndConditions
										termsAndConditions={{
											type: 'link',
											url: TcUrl,
										}}
										showCheckbox
										isChecked={termsAndConditionsCheckbox}
										setIsChecked={setTermsAndConditionsCheckbox}
										showError={showError}
									/>
								}
								loading={loading}
								orderSummary={orderSummary}
								lines={lines}
								handleRemoveItem={handleDeleteLine(false)}
							/>
							<ConditionalRender
								show={sendDialog}
								onTrue={() => (
									// it is used to display success and failure
									<SendDialogFiberPro
										hideDialog={() => {
											setSendDialog(false);
											setLines((lines) => lines.map((line) => ({ ...line, status: 'idle' })));
											loadQualtrics({
												activity_type: 'Add New Service - Fiber Pro',
												service_internet: lines.length,
											});
											history.push(`/dashboard/services`);
										}}
										sendRequest={(retry: boolean = false) => sendOneRequest(retry)}
										lines={orderEl.map((line, ind) => ({
											line: line.lineEl,
											status: lines[ind].status,
											requestId: lines[ind].requestId,
										}))}
									/>
								)}
							/>
						</>
					),
				},
			]}
			activeTab={activeTab}
			setActiveTab={setActiveTab}
			setScrollToButton={setScrollToButton}
			scrollToButton={scrollToButton}
		/>
	);
};

export default Index;
